import { useCallback, useEffect, useMemo, useState } from "react";
import { getSummarizeJobByName } from "./patientApi";
import {
  getSummarizationJobSoapNote,
  normalizeSummarizationJobs,
  sortSummarizationJobsNewestFirst,
} from "./summarizationJobUtils";

export const useSummarizationJobSelection = (patientId, summarization) => {
  const sortedJobs = useMemo(
    () =>
      sortSummarizationJobsNewestFirst(
        normalizeSummarizationJobs(summarization)
      ),
    [summarization]
  );

  const [selectedJobName, setSelectedJobName] = useState(null);
  const [aiNote, setAiNote] = useState("");
  const [aiNoteLoading, setAiNoteLoading] = useState(false);
  const [aiNoteError, setAiNoteError] = useState(null);

  const selectedJob = useMemo(
    () =>
      sortedJobs.find((job) => job.summarize_job_name === selectedJobName) ||
      null,
    [sortedJobs, selectedJobName]
  );

  const selectJob = useCallback((jobName) => {
    setSelectedJobName((current) => (current === jobName ? null : jobName));
  }, []);

  const clearSelection = useCallback(() => {
    setSelectedJobName(null);
  }, []);

  useEffect(() => {
    setSelectedJobName(null);
    setAiNote("");
    setAiNoteError(null);
    setAiNoteLoading(false);
  }, [patientId]);

  useEffect(() => {
    if (!selectedJob) {
      setAiNote("");
      setAiNoteError(null);
      setAiNoteLoading(false);
      return undefined;
    }

    const inlineNote = getSummarizationJobSoapNote(selectedJob);
    if (inlineNote) {
      setAiNote(inlineNote);
      setAiNoteError(null);
      setAiNoteLoading(false);
      return undefined;
    }

    const jobName = selectedJob.summarize_job_name;
    if (!jobName) {
      setAiNote("");
      setAiNoteError("No job name on record.");
      setAiNoteLoading(false);
      return undefined;
    }

    let cancelled = false;
    setAiNoteLoading(true);
    setAiNoteError(null);
    setAiNote("");

    (async () => {
      const data = await getSummarizeJobByName(jobName);
      if (cancelled) return;

      if (data.error) {
        setAiNoteError(data.error);
        setAiNote("");
      } else {
        const note = getSummarizationJobSoapNote(data) || "";
        setAiNote(note);
        if (!note) {
          setAiNoteError("No AI note on this summarization job.");
        }
      }
      setAiNoteLoading(false);
    })();

    return () => {
      cancelled = true;
    };
  }, [selectedJob]);

  return {
    sortedJobs,
    selectedJobName,
    selectedJob,
    selectJob,
    clearSelection,
    aiNote,
    aiNoteLoading,
    aiNoteError,
    isComparing: Boolean(selectedJobName),
  };
};
